/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility Create, update, archive and select default reusable assistants.
 * @state-owner TanStack Query mutation state
 * @allowed-imports app environment; TanStack Query; assistant query keys and model
 * @forbidden routes; components; direct generated SDK
 * @public-api via modules/assistants/index.ts
 * @side-effects authenticated assistant mutation requests outside demo mode
 * @naming-hook GET -> use<Resource>; POST -> useCreate<Resource>; PUT -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-path plural lowercase nouns; /default is an action sub-resource
 * @naming-body camelCase; never caller role or server-side identity fields
 * @tests modules/assistants/api/assistants.mutations.test.ts
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import type { AssistantRecord } from '../../../shared/lib/model';
import { assistantKeys, parseAssistant } from './assistants.queries';

const assistantsPath = '/api/v1/assistants';

function bodyFor(assistant: AssistantRecord, organizationId: string, idempotencyKey: string) {
  return {
    organizationId,
    name: assistant.name.trim(),
    tone: assistant.tone,
    persona: assistant.persona.trim(),
    hintLimit: assistant.hintsBeforeTeacher,
    forbiddenText: assistant.forbidden.trim(),
    idempotencyKey,
  };
}

export function useCreateAssistant() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assistant,
      organizationId,
      idempotencyKey,
    }: {
      activeContextId: string;
      organizationId: string;
      assistant: AssistantRecord;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) return assistant;
      return parseAssistant(
        await environment.apiClient.request<unknown>(assistantsPath, {
          method: 'POST',
          body: bodyFor(assistant, organizationId, idempotencyKey),
        }),
      );
    },
    onSuccess: (_result, input) =>
      queryClient.invalidateQueries({ queryKey: assistantKeys.all(input.activeContextId) }),
  });
}

export function useUpdateAssistant() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assistant,
      organizationId,
      idempotencyKey,
    }: {
      activeContextId: string;
      organizationId: string;
      assistant: AssistantRecord;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) return assistant;
      return parseAssistant(
        await environment.apiClient.request<unknown>(
          `${assistantsPath}/${encodeURIComponent(assistant.id)}`,
          {
            method: 'PUT',
            body: {
              ...bodyFor(assistant, organizationId, idempotencyKey),
              revision: assistant.revision,
            },
          },
        ),
      );
    },
    onSuccess: (_result, input) =>
      queryClient.invalidateQueries({ queryKey: assistantKeys.all(input.activeContextId) }),
  });
}

export function useDeleteAssistant() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assistant,
      organizationId,
      idempotencyKey,
    }: {
      activeContextId: string;
      organizationId: string;
      assistant: AssistantRecord;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) return assistant;
      return parseAssistant(
        await environment.apiClient.request<unknown>(
          `${assistantsPath}/${encodeURIComponent(assistant.id)}`,
          {
            method: 'DELETE',
            body: { organizationId, revision: assistant.revision, idempotencyKey },
          },
        ),
      );
    },
    onSuccess: (_result, input) =>
      queryClient.invalidateQueries({ queryKey: assistantKeys.all(input.activeContextId) }),
  });
}

export function useUpdateAssistantDefault() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assistantId,
      organizationId,
      idempotencyKey,
    }: {
      activeContextId: string;
      organizationId: string;
      assistantId: string;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) return assistantId;
      return parseAssistant(
        await environment.apiClient.request<unknown>(`${assistantsPath}/default`, {
          method: 'PUT',
          body: { organizationId, assistantId, idempotencyKey },
        }),
      );
    },
    onSuccess: (_result, input) =>
      queryClient.invalidateQueries({ queryKey: assistantKeys.all(input.activeContextId) }),
  });
}

export { bodyFor };
